import { AppVersion } from '@application/config';
import { eLanguageCode, languageCodeFromLocale, languageCodeMap } from '@shared/components/shortcuts/data-access';

export interface IOwnerCulture {
    DateTimeFormat: IDateTimeFormat,
    NumberFormat: INumberFormat
}

interface IDateTimeFormat {
    AMDesignator: string,
    Calendar: ICalendar,
    DateSeparator: string,
    FirstDayOfWeek: number,
    CalendarWeekRule: number,
    FullDateTimePattern: string,
    LongDatePattern: string,
    LongTimePattern: string,
    MonthDayPattern: string,
    PMDesignator: string,
    RFC1123Pattern: string,
    ShortDatePattern: string,
    ShortTimePattern: string,
    SortableDateTimePattern: string,
    TimeSeparator: string,
    UniversalSortableDateTimePattern: string,
    YearMonthPattern: string,
    AbbreviatedDayNames: string[],
    ShortestDayNames: string[],
    DayNames: string[],
    AbbreviatedMonthNames: string[],
    MonthNames: string[],
    IsReadOnly: boolean,
    NativeCalendarName: string,
    AbbreviatedMonthGenitiveNames: string[],
    MonthGenitiveNames: string[]
  }

interface ICalendar {
    MinSupportedDateTime: string,
    MaxSupportedDateTime: string,
    AlgorithmType: number,
    IsReadOnly: boolean,
    Eras: number[]
    TwoDigitYearMax: number
}

interface INumberFormat {
    CurrencyDecimalDigits: number,
    CurrencyDecimalSeparator: string,
    IsReadOnly: boolean,
    CurrencyGroupSizes: number[],
    NumberGroupSizes: number[],
    PercentGroupSizes: number[],
    CurrencyGroupSeparator: string,
    CurrencySymbol: string,
    NaNSymbol: string,
    CurrencyNegativePattern: number,
    NumberNegativePattern: number,
    PercentPositivePattern: number,
    PercentNegativePattern: number,
    NegativeInfinitySymbol: string,
    NegativeSign: string,
    NumberDecimalDigits: number,
    NumberDecimalSeparator: string,
    NumberGroupSeparator: string,
    CurrencyPositivePattern: number,
    PositiveInfinitySymbol: string,
    PositiveSign: string,
    PercentDecimalDigits: number,
    PercentDecimalSeparator: string,
    PercentGroupSeparator: string,
    PercentSymbol: string,
    PerMilleSymbol: string,
    NativeDigits: string[],
    DigitSubstitution: number
}

export interface ISystemTimeZones {
    SAPacificStandardTime: string,
    CentralBrazilianStandardTime: string,
    EUSouthAmericaStandardTime: string,
}

export type TSystemCultures = {
  Name: string,
  DisplayName: string,
  DateExample: string,
  TimeExample: string,
  DateTimeExample: string,
  NumberExample: string,
  CurrencyExample: string,
}

export type TValidationResult = {
  IsValid?: boolean;
  Errors?: string[];
  RuleSetsExecuted?: string[] | null;
};

export enum EPersonTreatment {
  Client = 0,
  Student = 1,
  Patient = 2,
  Member = 3,
  Associate = 4,
}

export type TPersonSettings = {
  PersonSettingsId: string;
  PersonTreatment: EPersonTreatment;
  EmailRequired: boolean;
  /** Nullable on the wire: `Prop<bool>?` in the backend `PersonSettingsViewModel`. */
  UserEmailRequired: boolean | null;
  PhoneRequired: boolean;
  AddressRequired: boolean;
  RegistrationRequired: boolean;
  FiscalIdentityRequired: boolean;
  IdentityDocumentRequired: boolean;
  AutomaticDeactivationDays: number;
  DisableWithoutSubscriptionDays: number;
  Operation?: any | null;
  ValidationResult?: TValidationResult;
  IsValid?: boolean;
  UpdatedOn: string;
};

export type TOperationException = {
  OperationExceptionId?: string;
  Day: string; // ISO date string
  Description: string;
  IsOpen: boolean;
  IsImported?: boolean;
  TenantId?: string;
  ValidationResult?: TValidationResult;
  IsValid?: boolean;
};

export type TOpeningHours = {
  TenantId: string;
  OperationsDaysOfWeek: TOperationsDaysOfWeek[];
  OperationPeriodId?: string;
  Operation?: any | null;
  ValidationResult?: TValidationResult;
  IsValid?: boolean;
  UpdatedOn: string;
};

export type TOperationsDaysOfWeek = {
  DayOfWeek: number;
  Periods: TPeriods[];
  OperationDayOfWeekId?: string;
  OperationPeriodId?: string;
  Operation?: any | null;
  ValidationResult?: TValidationResult;
  IsValid?: boolean;
};

export type TPeriods = {
  StartOn: string;
  EndOn: string;
  OperationDayOfWeekId?: string;
  PeriodId?: string;
  Operation?: any | null;
  ValidationResult?: TValidationResult;
  IsValid?: boolean;
};

export type TPrintReceiptCount = {
  TenantId?: string;
  CountPrinterReceipt?: number;
  UpdatedOn?: string;
  IsUpdatedPrinterReceipt?: boolean;
}

export type TCulture = {
  id: string;
  text: string;
}

export type TSpace = {
  SpaceId: string;
  icon: string;
  //ActivityId: string;
  Description: string;
  IsActive: boolean;
  IsIndoor: boolean;
  SpaceTypeId: string;
  SpaceTypeDescription: string;
  TenantId: string;
  UpdatedOn: string; // ISO date string
  AddedOn: string;   // ISO date string
};

export type TActivities = {
  ActivityId: string;
  Description: string;
  AddedBy?: string;
  Icon: string;
  Base: boolean;
  TenantId: string;
  IsSubscriptionEnabled?: boolean;
  UpdatedOn: string; // ISO date string
  AddedOn: string;   // ISO date string
  Workers?: TActivityWorker[];
};

export type TActivityWorker = {
  WorkerId?: string;
  PersonId: string;
  PersonName?: string;
  ActivityId?: string;
  CommissionAmount?: number;
  CommissionRate?: number;
  IsConsumption?: boolean;
};

export type TSpaceType = {
  Description: string;
  SpaceTypeId: string;
}

export type TActivitiesType = {
  Description: string;
  SpaceTypeId: string;
}

export type TOwnerSettings = {
  CertificateArchive: any | null;
  AppVersion?: AppVersion;
  Culture: string;
  IsValid: boolean;
  Logos: any | null;
  LanguageCode: eLanguageCode;
  Operation: any | null;
  OwnerSettingsId: string;
  PrintLogoStorageId: string;
  RectangularLogoStorageId: string;
  SquareLogoStorageId: string;
  Timezone: string;
  UpdatedOn: string;
  Currency: string;
  MaxCapacity?: number | null;
  AverageStayMinutes?: number | null;
}

export type TUpdateRegionalSettings = {
  Culture: string;
  Timezone: string;
  OwnerSettingsId: string;
}

export type TSpooler = {
  DeviceId: string;
  PairingCode: string;
  HardwareId: string;
  DeviceName: string;
  UseQueue: boolean;
  Default: boolean;
  Type: ('P'| 'T' | 'R')[],
  CreatedBy: string;
  CreatedAt: string;
}

export type TOData<T> = {
  '@odata.context': string;
  '@odata.count': number;
  value: T[];
}

export type TCategory = {
  FinancialCategoryId: string;
  Description: string;
  RangeStart: number;
  RangeEnd: number;
  TenantId: string;
  Icon: string;
}

export type TCategoryHistory = {
  CategoryHistoryId: string;
  AverageTicket: number;
  Year: number;
  Month: number;
  FinancialCategoryId: string;
  PersonId: string;
}

export type TAverageTicket = {
  AverageTicketSettingsId: string;
  CalculateBySales: boolean;
  CalculateByServices: boolean;
  CalculateBySubscriptions: boolean;
  MonthsRangeToCalculate: number;
  TenantId: string;
}

export const flatIconsArray = [
  'ruby',
  'sapphire',
  'emerald',
  'diamond',
  'quartz',
  'amethyst'
]

export const flatIcons: Record<string, string> = {
    ruby: '<svg class="flatIcon" id="Capa_1" style="width:17px;  height:100%;" enable-background="new 0 0 512 512" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><g><path d="m512 174.176-256 294.581-256-294.581 256-35.197z" fill="#ce0045"/><path d="m512 174.176-256 294.581v-329.778z" fill="#a00031"/><path d="m384.502 108.71-30.308 65.466-98.194-58.559-98.194 58.559-29.806-65.466v-65.465h256z" fill="#ce0045"/><path d="m384.502 108.71-30.308 65.466-98.194-58.559v-72.372h128z" fill="#a00031"/><path d="m354.194 174.176h-196.388l98.194-130.931z" fill="#fe7e52"/><path d="m354.194 174.176h-98.194v-130.931z" fill="#f53241"/><path d="m128 43.243-128 130.929h157.804z" fill="#fe7e52"/><path d="m354.196 174.172h157.804l-128-130.929z" fill="#f53241"/><path d="m354.194 174.176-98.194 294.581-98.194-294.581z" fill="#f53241"/><path d="m354.194 174.176-98.194 294.581v-294.581z" fill="#ce0045"/></g></svg>',
    sapphire: '<svg class="flatIcon" id="Capa_1" style="width:17px;  height:100%;"  enable-background="new 0 0 512 512" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><g><g><path d="m99.76 256 44.502 111.742 111.738 44.498 83.203-155.397-83.203-157.083-127.005 45.261z" fill="#006eff"/><path d="m256 99.76v312.48l114.878-44.862 41.362-111.378-41.362-105.154z" fill="#0050c1"/></g><path d="m512 256v56.47l-107.78 107.78h-68.02l-10.09-78.12 86.13-86.13 55.658-38.881z" fill="#006eff"/><path d="m512 199.53v56.47h-99.76l-78.12-78.12 2.08-78.11h76.04z" fill="#00317e"/><path d="m256 412.24 53.724 39.76-53.724 60h-56.47l-99.76-99.76 9.148-70.11 68.962-8.01z" fill="#0050c1"/><path d="m404.22 420.25-91.75 91.75h-56.47v-99.76l70.11-70.11z" fill="#00317e"/><path d="m169.87 185.89-70.11 70.11-32.398 44.197-67.362-44.197v-56.47l91.75-91.75 78.12 14.462z" fill="#0050c1"/><path d="m177.88 334.12-78.11 78.12h-.01l-99.76-99.77v-56.47h99.76z" fill="#3aaaff"/><path d="m412.24 99.77v-.01" fill="#ffe566"/><path d="m256 0 44.37 50.181-44.37 49.579-86.13 86.13-78.12-78.11 107.78-107.78z" fill="#3aaaff"/><path d="m412.24 99.77-78.12 78.11-78.12-78.12v-99.76h56.47z" fill="#006eff"/></g></svg>',
    emerald: '<svg class="flatIcon" id="Capa_1" style="width:17px;  height:100%;"  enable-background="new 0 0 370 370" height="512" viewBox="0 0 370 370" width="512" xmlns="http://www.w3.org/2000/svg"><g><g><path d="m75 76v222h111l21-110-21-112z" fill="#00dc80"/><path d="m186 76h113v222h-113z" fill="#6fbe6b"/></g><g><path d="m87.47 122.28 34.32-34.28h.552l-57.252-87-65.09 65.03 87.47 56.973z" fill="#6fbe6b"/><path d="m248.21 88 34.32 34.28v.723l87.47-56.973-65.09-65.03-57.252 87z" fill="#4a9f46"/><path d="m87.47 123.003-87.47-56.973v237.98l87.47-57.013z" fill="#00ff93"/><path d="m121.81 282-34.34-34.26v-.743l-87.47 57.013 65.13 64.99 57.212-87z" fill="#6fbe6b"/><path d="m282.53 123.003v123.994l87.47 57.013v-237.98z" fill="#00dc80"/><path d="m282.53 247.74-34.34 34.26h-.532l57.212 87 65.13-64.99-87.47-57.013z" fill="#4a9f46"/></g><g><path d="m122.342 282-57.212 87h120.87l14-41-14-46z" fill="#73cb7b"/><path d="m247.658 282h-61.658v87h118.87z" fill="#58b354"/></g><g><path d="m65.09 1 57.252 87h63.658l20-41-20-46z" fill="#73cb7b"/><path d="m304.91 1h-118.91v87h61.658z" fill="#58b354"/></g></g></svg>',
    diamond: '<svg class="flatIcon" version="1.1" style="width:17px;  height:100%;"  id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 511.999 511.999" style="enable-background:new 0 0 511.999 511.999;" xml:space="preserve"><path style="fill:#A0EBFF;" d="M134.047,194.408H15.118c-6.096,0-11.595-3.661-13.947-9.285c-2.351-5.624-1.097-12.11,3.184-16.449 L124.795,46.56c2.841-2.881,6.718-4.502,10.764-4.502h59.464c5.238,0,10.103,2.711,12.858,7.167 c2.755,4.455,3.007,10.018,0.667,14.705l-60.976,122.114C145.013,191.17,139.776,194.408,134.047,194.408z"/><path style="fill:#73C8F0;" d="M496.881,194.408H377.952c-5.73,0-10.966-3.238-13.526-8.364L303.45,63.93 c-2.34-4.687-2.087-10.25,0.667-14.705s7.621-7.167,12.858-7.167h59.464c4.046,0,7.923,1.622,10.764,4.502l120.441,122.114 c4.28,4.34,5.535,10.827,3.184,16.449C508.476,190.748,502.977,194.408,496.881,194.408z"/><path style="fill:#D2F0FF;" d="M117.142,178.897l77.881-136.839h121.953l77.642,136.839H117.142z"/><path style="fill:#0073AA;" d="M361.594,178.897L242.175,448.703c-3.025,6.832-0.604,14.85,5.698,18.866 c2.505,1.596,5.323,2.371,8.119,2.371c4.239,0,8.426-1.779,11.389-5.168L508.262,189.24c2.546-2.912,3.818-6.619,3.722-10.344 H361.594V178.897z"/><path style="fill:#41B4E6;" d="M150.405,178.897H0.014c-0.096,3.724,1.176,7.432,3.722,10.344l240.881,275.532 c2.963,3.389,7.151,5.168,11.389,5.168c2.796,0,5.615-0.774,8.119-2.371c6.3-4.016,8.722-12.034,5.698-18.866L150.405,178.897z"/><path style="fill:#A0EBFF;" d="M117.142,178.897l125.034,282.044c2.421,5.471,7.841,8.999,13.824,8.999s11.403-3.528,13.824-8.999 l124.795-282.044H117.142z"/><polygon style="fill:#41B4E6;" points="255.88,178.897 394.618,178.897 316.975,42.058 255.88,42.058 "/><path style="fill:#73C8F0;" d="M256,469.94c5.983,0,11.403-3.528,13.824-8.999l124.794-282.044H256V469.94z"/><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g></svg>',
    quartz: '<svg class="flatIcon" version="1.1" style="width:17px;  height:100%;" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512" style="enable-background:new 0 0 512 512;" xml:space="preserve"><polygon style="fill:#FFE182;" points="360.129,172.138 256,472.276 512,172.138 "/><g> <polygon style="fill:#FFCD73;" points="105.931,39.724 0,172.138 151.871,172.138 	"/> <polygon style="fill:#FFCD73;" points="360.129,172.138 512,172.138 406.069,39.724 	"/> <polygon style="fill:#FFCD73;" points="360.129,172.138 256,39.724 151.871,172.138 	"/></g><polygon style="fill:#FFAA64;" points="256,39.724 105.931,39.724 151.871,172.138 "/><polygon style="fill:#FFE182;" points="406.069,39.724 256,39.724 360.129,172.138 "/><polygon style="fill:#FFAA64;" points="151.871,172.138 256,472.276 360.129,172.138 "/><polygon style="fill:#FF8C5A;" points="0,172.138 256,472.276 151.871,172.138 "/><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g><g></g></svg>',
    amethyst: '<svg class="flatIcon" id="Capa_1" style="width:17px;  height:100%;"  enable-background="new 0 0 512 512" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><g><path d="m482.009 31.705h-455.017v453.303h437.345c3.279-.679 14.248 2.608 16.825.679 1.424-1.065 2.715-2.297 3.847-3.665 2.283-2.76 1.701-9.805 2.457-13.454 29.757-144.197 24.301-292.666-5.457-436.863z" fill="#af66da"/><path d="m489.682 39.705c-.757-3.657-2.394-6.972-4.674-9.727-.508-.614-3.108.858-3.678.302-.439-.428 1.164-2.898.691-3.289-2.764-2.286-6.084-3.919-9.727-4.674-8.549-1.764-17.098-3.416-25.646-4.971 6.514 3.013 11.387 8.965 12.891 16.228 30.601 148.283 30.601 296.567 0 444.85-1.499 7.236-6.355 13.205-12.887 16.227 8.548-1.555 17.095-3.206 25.643-4.97 3.651-.756 6.968-2.392 9.727-4.674.544-.45-2.024-2.983-1.525-3.482.498-.498 4.063 1.039 4.512.496 2.283-2.76 3.918-6.078 4.673-9.727 29.757-144.196 29.757-288.392 0-432.589z" fill="#9857bd"/><path d="m22.318 472.295c.755 3.647 2.384 6.964 4.674 9.726l194.341-226.021-194.341-224.295c-2.278 2.75-3.918 4.341-4.674 8-29.757 144.197-29.757 288.393 0 432.59z" fill="#ebd5f6"/><path d="m482.022 485.008c-2.762 2.278-6.079 3.918-9.727 4.674-144.196 29.758-288.393 29.758-432.589 0-3.648-.755-9.951-2.384-12.714-4.674l229.008-79.288z" fill="#cb97e7"/><path d="m472.295 489.682c3.648-.755 6.965-2.396 9.727-4.674l-22.238-7.801c-.083.406-.16.812-.244 1.218-1.499 7.236-6.355 13.205-12.887 16.227 8.547-1.555 17.094-3.206 25.642-4.97z" fill="#cb97e7"/><path d="m482.009 26.993-226.009 79.287-226.009-79.287c2.762-2.29 6.079-3.919 9.715-4.674 144.196-29.758 288.393-29.758 432.589 0 3.635.755 6.952 2.384 9.714 4.674z" fill="#e5caf3"/><path d="m472.295 22.318c-8.549-1.764-17.098-3.416-25.646-4.971 6.514 3.013 11.387 8.966 12.891 16.228.084.405.16.812.243 1.217l22.227-7.799c-2.763-2.29-6.08-3.919-9.715-4.675z" fill="#d9b1ee"/><path d="m485.008 29.978-79.288 226.022-123.305-46.282-26.415-103.438 226.022-79.288c1.097.897 2.089 1.889 2.986 2.986z" fill="#cb97e7"/><path d="m482.022 26.992-22.239 7.8c3.007 14.652 5.706 29.306 8.116 43.959l17.109-48.772c-.897-1.098-1.889-2.09-2.986-2.987z" fill="#af66da"/><path d="m256 106.28-33.195 130.021-116.525 19.699-79.288-224.295c.897-1.098 1.889-3.816 2.987-4.713z" fill="#d9b1ee"/><path d="m485.009 482.022c-.897 1.098-1.889 2.089-2.987 2.986l-226.022-79.288 34.107-116.538 115.613-33.182z" fill="#bd80e1"/><path d="m467.899 433.25c-2.41 14.652-5.108 29.305-8.116 43.957l22.238 7.801c1.098-.897 2.09-1.888 2.987-2.986z" fill="#af66da"/><path d="m256 405.72-229.008 79.288c-1.098-.897.898-1.889.001-2.987l79.287-226.021 115.564 14.989z" fill="#d9b1ee"/><path d="m150.132 150.132h211.736v211.736h-211.736z" fill="#ebd5f6" transform="matrix(.707 -.707 .707 .707 -106.039 256)"/></g></svg>'
}

export const emojiHexArray = [
  '&#127941;',
  '&#129351;',
  '&#129352;',
  '&#127894;&#65039;',
  '&#129353;',
  '&#127942;',
  '&#128993',
  '&#x1F535;',
  '&#x1F534;',
  '&#x26AB;',
  '&#x26AA;',
  '&#x1F536;',
  '&#x1F537;',
  '&#128640;',
  '&#128142;',
  '&#128293;',
  '&#x1F4B8;',
  '&#x1F4B2;',
  '&#x1F4B0;',
  '&#x1F4B5;',
  '&#129535;',
  '&#9728;&#65039;',
  '&#127761;',
  '&#127769;',
  '&#127776;',
  '&#9889;',
  '&#127777;&#65039;',
  '&#9760;',
  '&#128128;',
  '&#11088;',
  '&#x1F5A4;',
  '&#x1F499;',
  '&#x1F49A;',
  '&#x1F49B;',
  '&#x1F49C;',
  '&#x1F9E1;',
  '&#x1F90D;',
  '&#x1F90E;',
  '&#x2763;',
  '&#x2764;',
  '&#x2765;',
  '&#x1F394;',
  '&#x1F493;',
  '&#x1F494;',
  '&#x1F496;',
  '&#x1F497;',
  '&#x1F495;',
  '&#x1F49E;',
  '&#x1F498;',
  '&#x1F49D;',
  '&#x1F49F;',
  '&#x1F48C;',
  '&#x1F970;',
  '&#x1F60D;',
  '&#x1F618;',
  '&#x1F63B;',
  '&#x1F491;',
  '&#x1F48F;',
  '&#127937;',
  '&#128681;',
  '&#127884;',
  '&#127988;',
  '&#127987;',
  '&#127987;&#65039;&#8205;&#127752;',
  '&#127988;&#8205;&#9760;&#65039;',
  '&#128512',
  '&#128515',
  '&#128516',
  '&#128513',
  '&#128518',
  '&#128517',
  '&#129315',
  '&#128514',
  '&#128578',
  '&#128579',
  '&#128521',
  '&#128522',
  '&#128519',
  '&#129392',
  '&#128525',
  '&#129321',
  '&#128536',
  '&#128535',
  '&#9786',
  '&#128538',
  '&#128537',
  '&#128523',
  '&#128539',
  '&#128540',
  '&#129322',
  '&#128541',
  '&#129297',
  '&#129303',
  '&#129325',
  '&#129323',
  '&#129300',
  '&#129296',
  '&#129320',
  '&#128528',
  '&#128529',
  '&#128566',
  '&#128527',
  '&#128530',
  '&#128580',
  '&#128556',
  '&#129317',
  '&#128524',
  '&#128532',
  '&#128554',
  '&#129316',
  '&#128564',
  '&#128567',
  '&#129298',
  '&#129301',
  '&#129314',
  '&#129326',
  '&#129319',
  '&#129397',
  '&#129398',
  '&#129396',
  '&#128565',
  '&#129327',
  '&#129312',
  '&#129395',
  '&#128526',
  '&#129299',
  '&#129488',
  '&#128533',
  '&#128543',
  '&#128577',
  '&#9785',
  '&#128558',
  '&#128559',
  '&#128562',
  '&#128563',
  '&#129402',
  '&#128550',
  '&#128551',
  '&#128552',
  '&#128560',
  '&#128549',
  '&#128546',
  '&#128557',
  '&#128561',
  '&#128534',
  '&#128547',
  '&#128542',
  '&#128531',
  '&#128553',
  '&#128555',
  '&#128548',
  '&#128545',
  '&#128544',
  '&#129324',
  '&#128520',
  '&#128127',
  '&#128128',
  '&#9760',
  '&#128169',
  '&#129313',
  '&#128121',
  '&#128122',
  '&#128123',
  '&#128125',
  '&#128126',
  '&#129302',
  '&#128570',
  '&#128568',
  '&#128569',
  '&#128571',
  '&#128572',
  '&#128573',
  '&#128576',
  '&#128575',
  '&#128574',
  '&#128584',
  '&#128585',
  '&#128586',
  '&#128175',
  '&#128162',
  '&#128165',
  '&#128171',
  '&#128166',
  '&#128168',
  '&#128371',
  '&#128163',
  '&#128172',
  '&#128488',
  '&#128495',
  '&#128173',
  '&#128164',
  '&#128075',
  '&#129306',
  '&#128400',
  '&#9995',
  '&#128076',
  '&#9996',
  '&#129310',
  '&#129311',
  '&#129304',
  '&#129305',
  '&#128072',
  '&#128073',
  '&#128070',
  '&#128405',
  '&#128071',
  '&#9757',
  '&#128077',
  '&#128078',
  '&#9994',
  '&#128074',
  '&#129307',
  '&#129308',
  '&#128079',
  '&#128588',
  '&#128080',
  '&#129330',
  '&#129309',
  '&#128591',
  '&#9997',
  '&#128133',
  '&#129331',
  '&#128170',
  '&#129461',
  '&#129462',
  '&#128066',
  '&#128067',
  '&#129504',
  '&#129463',
  '&#129460',
  '&#128064',
  '&#128065',
  '&#128069',
  '&#128068',
  '&#128483',
  '&#128100',
  '&#x1F600;'
]

export const emojiHex: Record<string, string> = emojiHexArray.reduce((acc, value) => {
    acc[value] = value;
    return acc
  }, {});

/**
 * Normaliza valores de `Timezone` guardados em formatos antigos para o id que a
 * lista de opções da tela Cultura realmente contém.
 *
 * O `/SettingsOptions/GetSystemTimeZones` devolve ids **IANA**, e o
 * `culture.component` compara o valor do formulário diretamente contra esses
 * ids. Uma conta que ainda tenha guardado a label legada — ou o id Windows
 * `'E. South America Standard Time'` — não bate com nenhuma opção e o campo
 * aparece VAZIO, embora o `Validators.required` passe (o control tem valor).
 * Mapear para IANA é o que faz o valor ser encontrado e exibido como válido.
 *
 * Não remover a entrada Windows por parecer "formato vivo": é precisamente o
 * caso que o mapa existe para resolver. O `DateTimeService.getUserZone()` lida
 * com as duas grafias por ramos diferentes — Windows pelo `findIana()`, IANA
 * pelo fallback que valida o próprio valor — portanto nenhuma das duas quebra o
 * fuso da app, e a normalização só afeta a apresentação e o que se volta a
 * gravar.
 */
export const timezoneByLegacyTimezones: Record<string, string> = {
  '(UTC-03:00) Brasília, Salvador, Araguaina, Fortaleza': 'America/Sao_Paulo',
  '(UTC-04:00) Cuiabá, Manaus': 'America/Cuiaba',
  '(UTC-05:00) Rio Branco, Acre': 'America/Rio_Branco',
  'E. South America Standard Time': 'America/Sao_Paulo',
};

export function mapOwnerSettings(data: TOwnerSettings): TOwnerSettings {
  // A 204 leaves the body null — mapping it would throw and fail the request.
  if (!data) return data;

  const newTimezone = timezoneByLegacyTimezones[data.Timezone];
  if (newTimezone) {
    data.Timezone = newTimezone;
  }
  const hasValidLanguage = languageCodeMap[data.LanguageCode];
  const languageByCulture = languageCodeFromLocale[data.Culture];
  if (!hasValidLanguage && !!languageByCulture) {
    data.LanguageCode = languageByCulture;
  }
  return data;
}
